import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sessionAPI, applicationAPI, uploadAPI } from '../../services/api';
import { getCountryCodeFromPhone, getIdentificationFieldsForCountry, formatIdentificationValue, validateIdentificationValue } from '../../utils/countryIdentification';
import toast from 'react-hot-toast';

const ApplicationForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();

    // State management
    const [currentStage, setCurrentStage] = useState(1);
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [error, setError] = useState('');
    const [countryCode, setCountryCode] = useState(null);
    const [identificationFields, setIdentificationFields] = useState([]);
    const [identificationData, setIdentificationData] = useState({});

    // Stage data
    const [stage1, setStage1] = useState({
        first_name: '',
        last_name: '',
        email: '',
        contact_number: ''
    });

    const [stage2, setStage2] = useState({
        highest_qualification: '',
        institution_name: '',
        year_of_passing: '',
        address_country: '',
        address_state: '',
        address_city: '',
        date_of_birth: '',
        gender: '',
        marital_status: '',
        number_of_kids: '',
        current_occupation: ''
    });

    const [stage3, setStage3] = useState({
        government_id_path: '',
        educational_certificates_path: '',
        passport_photo_path: ''
    });

    const [stage4, setStage4] = useState({
        motivation_text: '',
        career_goals: '',
        financial_background: '',
        code_of_conduct_agreed: false
    });

    // Load active sessions - runs once on mount
    useEffect(() => {
        let isActive = true;

        const loadSessions = async () => {
            try {
                setSessionsLoading(true);
                const response = await sessionAPI.getActive();
                const sessionsList = response.sessions || [];

                if (!isActive) return;

                setSessions(sessionsList);

                // Determine session ID from URL param or first available
                const sessionParam = searchParams.get('session');
                let targetSessionId = null;

                if (sessionParam) {
                    const sessionExists = sessionsList.some(s => s.id === parseInt(sessionParam));
                    if (sessionExists) {
                        targetSessionId = parseInt(sessionParam);
                    }
                }

                if (!targetSessionId && sessionsList.length > 0) {
                    targetSessionId = sessionsList[0].id;
                }

                if (targetSessionId) {
                    setSessionId(targetSessionId);
                }

                setSessionsLoading(false);
            } catch (err) {
                console.error('Failed to load sessions:', err);
                if (isActive) {
                    setSessionsLoading(false);
                    setError('Failed to load sessions. Please try again later.');
                    setSessions([]);
                }
            }
        };

        loadSessions();

        return () => {
            isActive = false;
        };
    }, []); // Empty dependency - runs once

    // Handle country code and identification fields based on phone number
    const previousPhoneRef = useRef(null);

    useEffect(() => {
        const phoneToUse = user?.phone || stage1.contact_number;
        
        if (!phoneToUse || phoneToUse === previousPhoneRef.current) {
            return;
        }

        previousPhoneRef.current = phoneToUse;
        const code = getCountryCodeFromPhone(phoneToUse);
        const fields = getIdentificationFieldsForCountry(code);

        setCountryCode(code);
        setIdentificationFields(fields.fields);

        // Initialize identification data
        setIdentificationData(prevData => {
            const newData = {};
            fields.fields.forEach(field => {
                newData[field.name] = prevData[field.name] || '';
            });
            return newData;
        });
    }, [user?.phone, stage1.contact_number]);

    // Pure validation function (no side effects) - for use in render
    const isStageValid = (stage) => {
        switch(stage) {
            case 1:
                return !!(stage1.first_name && stage1.last_name && stage1.email);
            case 2:
                return !!(stage2.highest_qualification && stage2.institution_name && stage2.year_of_passing && 
                    stage2.address_country && stage2.address_state && stage2.address_city && 
                    stage2.date_of_birth && stage2.marital_status && stage2.current_occupation);
            case 3:
                // Only passport photo is mandatory; government ID and educational certificates are optional
                if (!stage3.passport_photo_path) {
                    return false;
                }
                // Validate identification fields
                for (const field of identificationFields) {
                    if (field.required && !identificationData[field.name]) {
                        return false;
                    }
                }
                return true;
            case 4:
                return !!(stage4.motivation_text && stage4.career_goals && stage4.code_of_conduct_agreed);
            default:
                return true;
        }
    };

    // Validation function with error messages (for submission)
    const validateStage = (stage) => {
        switch(stage) {
            case 1:
                if (!stage1.first_name || !stage1.last_name || !stage1.email) {
                    setError('Please fill in all required fields in Stage 1');
                    return false;
                }
                return true;
            case 2:
                if (!stage2.highest_qualification || !stage2.institution_name || !stage2.year_of_passing || 
                    !stage2.address_country || !stage2.address_state || !stage2.address_city || 
                    !stage2.date_of_birth || !stage2.marital_status || !stage2.current_occupation) {
                    setError('Please fill in all required fields in Stage 2');
                    return false;
                }
                return true;
            case 3:
                // Only passport photo is mandatory; government ID and educational certificates are optional
                if (!stage3.passport_photo_path) {
                    setError('Please upload your passport photo in Stage 3');
                    return false;
                }
                // Validate identification fields
                for (const field of identificationFields) {
                    if (field.required && !identificationData[field.name]) {
                        setError(`Please fill in the required identification field: ${field.label}`);
                        return false;
                    }
                }
                return true;
            case 4:
                if (!stage4.motivation_text || !stage4.career_goals || !stage4.code_of_conduct_agreed) {
                    setError('Please complete all required fields in Stage 4');
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    // Save application draft
    const saveApplication = async () => {
        setLoading(true);
        try {
            const data = {
                session_id: sessionId,
                stage: currentStage,
                data: {
                    ...stage1,
                    ...stage2,
                    ...stage3,
                    ...stage4,
                    country_code: countryCode,
                    identification_data: JSON.stringify(identificationData)
                }
            };
            await applicationAPI.createOrUpdate(data);
        } catch (err) {
            setError(err.message || 'Failed to save application');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Submit final application
    const submitApplication = async () => {
        // Validate all stages
        if (!validateStage(1) || !validateStage(2) || !validateStage(3) || !validateStage(4)) {
            setError('Please complete all stages before submitting');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Save all data first
            await saveApplication();

            // Get the application and submit
            const response = await applicationAPI.getMyApplications();
            const myApp = response.applications?.find(app => app.session_id === sessionId);

            if (myApp) {
                await applicationAPI.submit(myApp.id);
                toast.success('Application submitted successfully!');
                setTimeout(() => {
                    navigate('/dashboard?application_submitted=true');
                }, 2000);
            } else {
                setError('No application found to submit. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit application');
            toast.error(err.message || 'Failed to submit application');
        } finally {
            setLoading(false);
        }
    };

    // Handle stage navigation
    const handleStageSubmit = async () => {
        if (!validateStage(currentStage)) {
            return;
        }

        if (currentStage < 4) {
            try {
                await saveApplication();
                setError('');
                setCurrentStage(currentStage + 1);
                toast.success(`Stage ${currentStage} saved successfully!`);
            } catch (err) {
                // Error already set in saveApplication
            }
        } else {
            await submitApplication();
        }
    };

    // Render functions for each stage
    const renderStage1 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 1: Basic Details</h3>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">First Name *</label>
                    <input
                        type="text"
                        value={stage1.first_name}
                        onChange={(e) => setStage1({...stage1, first_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <input
                        type="text"
                        value={stage1.last_name}
                        onChange={(e) => setStage1({...stage1, last_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                    type="email"
                    value={stage1.email}
                    onChange={(e) => setStage1({...stage1, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Contact Number</label>
                <input
                    type="tel"
                    value={stage1.contact_number}
                    onChange={(e) => setStage1({...stage1, contact_number: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                />
            </div>
        </div>
    );

    const renderStage2 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 2: Educational & Personal Details</h3>
            <div>
                <label className="block text-sm font-medium mb-1">Highest Qualification *</label>
                <select
                    value={stage2.highest_qualification}
                    onChange={(e) => setStage2({...stage2, highest_qualification: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                >
                    <option value="">Select...</option>
                    <option value="ssce">SSCE</option>
                    <option value="nd">ND</option>
                    <option value="hnd">HND</option>
                    <option value="bachelor">Bachelor's Degree</option>
                    <option value="master">Master's Degree</option>
                    <option value="phd">PhD</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Institution Name *</label>
                <input
                    type="text"
                    value={stage2.institution_name}
                    onChange={(e) => setStage2({...stage2, institution_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Year of Passing *</label>
                <input
                    type="number"
                    value={stage2.year_of_passing}
                    onChange={(e) => setStage2({...stage2, year_of_passing: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    min="1990"
                    max={new Date().getFullYear()}
                    required
                />
            </div>
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Country *</label>
                    <input
                        type="text"
                        value={stage2.address_country}
                        onChange={(e) => setStage2({...stage2, address_country: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">State *</label>
                    <input
                        type="text"
                        value={stage2.address_state}
                        onChange={(e) => setStage2({...stage2, address_state: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">City *</label>
                    <input
                        type="text"
                        value={stage2.address_city}
                        onChange={(e) => setStage2({...stage2, address_city: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                    <input
                        type="date"
                        value={stage2.date_of_birth}
                        onChange={(e) => setStage2({...stage2, date_of_birth: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Gender</label>
                    <select
                        value={stage2.gender}
                        onChange={(e) => setStage2({...stage2, gender: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                    >
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Marital Status *</label>
                    <select
                        value={stage2.marital_status}
                        onChange={(e) => setStage2({...stage2, marital_status: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        required
                    >
                        <option value="">Select...</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                        <option value="separated">Separated</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Number of Kids</label>
                    <input
                        type="number"
                        value={stage2.number_of_kids}
                        onChange={(e) => setStage2({...stage2, number_of_kids: e.target.value})}
                        className="w-full px-3 py-2 border rounded"
                        min="0"
                        placeholder="Enter number of children"
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Current Occupation *</label>
                <input
                    type="text"
                    value={stage2.current_occupation}
                    onChange={(e) => setStage2({...stage2, current_occupation: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="e.g., Student, Software Developer, Teacher, Unemployed, etc."
                    required
                />
            </div>
        </div>
    );

    const renderStage3 = () => {
        const countryInfo = getIdentificationFieldsForCountry(countryCode);
        
        return (
            <div className="space-y-4">
                <h3 className="text-xl font-bold">Stage 3: Identification & Documents</h3>
                <p className="text-gray-600">
                    Based on your country ({countryInfo.country}), please provide the following identification details:
                </p>
                
                {/* Country-specific Identification Fields */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-blue-900 mb-3">Identification Details ({countryInfo.country})</h4>
                    <div className="space-y-4">
                        {identificationFields.map((field) => {
                            const value = identificationData[field.name] || '';
                            const isValid = !value || !field.validator || validateIdentificationValue(field.name, value);
                            const hasError = value && field.validator && !isValid;
                            
                            return (
                                <div key={field.name}>
                                    <label className="block text-sm font-medium mb-1">
                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type={field.type}
                                        value={value}
                                        onChange={(e) => {
                                            const formattedValue = field.validator 
                                                ? formatIdentificationValue(field.name, e.target.value)
                                                : e.target.value;
                                            setIdentificationData({
                                                ...identificationData,
                                                [field.name]: formattedValue
                                            });
                                        }}
                                        placeholder={field.placeholder}
                                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                                            hasError 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'focus:ring-blue-500'
                                        }`}
                                        required={field.required}
                                        style={field.name === 'pan_card' || field.name === 'voter_id' || field.name === 'drivers_license' || field.name === 'national_insurance' ? { textTransform: 'uppercase' } : {}}
                                    />
                                    {field.validator && field.validator.formatHint && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {field.validator.formatHint}
                                        </p>
                                    )}
                                    {hasError && (
                                        <p className="text-xs text-red-500 mt-1">
                                            Invalid format. {field.validator?.formatHint}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Document Uploads */}
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Document Uploads</h4>
                    <div>
                        <label className="block text-sm font-medium mb-1">Government ID Document (optional)</label>
                        <p className="text-xs text-gray-500 mb-2">Upload a copy of your {identificationFields[0]?.label || 'government ID'}</p>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file);
                                        setStage3({...stage3, government_id_path: response.file.filename});
                                        toast.success('Government ID uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                        />
                        {stage3.government_id_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.government_id_path}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Educational Certificates (optional)</label>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file);
                                        setStage3({...stage3, educational_certificates_path: response.file.filename});
                                        toast.success('Educational certificates uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                        />
                        {stage3.educational_certificates_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.educational_certificates_path}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Passport Photo *</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    try {
                                        setLoading(true);
                                        const response = await uploadAPI.uploadFile(file);
                                        setStage3({...stage3, passport_photo_path: response.file.filename});
                                        toast.success('Passport photo uploaded successfully');
                                    } catch (err) {
                                        toast.error(err.message || 'Failed to upload file');
                                        e.target.value = '';
                                    } finally {
                                        setLoading(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 border rounded"
                            required
                        />
                        {stage3.passport_photo_path && (
                            <p className="text-xs text-green-600 mt-1">✓ File uploaded: {stage3.passport_photo_path}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderStage4 = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-bold">Stage 4: Motivational Questions</h3>
            <div>
                <label className="block text-sm font-medium mb-1">Why do you want this scholarship? *</label>
                <textarea
                    value={stage4.motivation_text}
                    onChange={(e) => setStage4({...stage4, motivation_text: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Career Goals (5 years) *</label>
                <textarea
                    value={stage4.career_goals}
                    onChange={(e) => setStage4({...stage4, career_goals: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Financial Background</label>
                <textarea
                    value={stage4.financial_background}
                    onChange={(e) => setStage4({...stage4, financial_background: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                />
            </div>
            <div className="flex items-center">
                <input
                    type="checkbox"
                    checked={stage4.code_of_conduct_agreed}
                    onChange={(e) => setStage4({...stage4, code_of_conduct_agreed: e.target.checked})}
                    className="mr-2"
                    required
                />
                <label className="text-sm">I agree to the Code of Conduct *</label>
            </div>
        </div>
    );

    // Main render
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-6 text-gray-900">Scholarship Application</h1>

                {sessionsLoading ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading available sessions...</p>
                    </div>
                ) : !sessionId && sessions.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8">
                        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                            <p className="font-semibold">No active sessions available</p>
                            <p className="text-sm mt-1">There are currently no scholarship sessions open for applications. Please check back later or contact support for more information.</p>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                ) : sessionId ? (
                    <>
                        <div className="mb-6">
                            <div className="flex justify-between mb-2">
                                {[1, 2, 3, 4].map((stage) => {
                                    const isCompleted = stage < currentStage;
                                    const isCurrent = stage === currentStage;
                                    const canAccess = stage === 1 || (stage > 1 && isStageValid(stage - 1));
                                    
                                    return (
                                        <div
                                            key={stage}
                                            onClick={() => {
                                                if (canAccess && stage <= currentStage) {
                                                    setCurrentStage(stage);
                                                    setError('');
                                                }
                                            }}
                                            className={`flex-1 text-center py-2 cursor-pointer transition-colors ${
                                                isCurrent
                                                    ? 'bg-blue-600 text-white'
                                                    : isCompleted
                                                    ? 'bg-green-500 text-white'
                                                    : canAccess
                                                    ? 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                            title={!canAccess ? 'Complete previous stages first' : ''}
                                        >
                                            Stage {stage}
                                            {isCompleted && <span className="ml-1">✓</span>}
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-600 text-center mt-2">
                                Complete each stage in order. You cannot skip stages.
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                                {error}
                            </div>
                        )}

                        <div className="bg-white p-6 rounded-lg shadow">
                            {currentStage === 1 && renderStage1()}
                            {currentStage === 2 && renderStage2()}
                            {currentStage === 3 && renderStage3()}
                            {currentStage === 4 && renderStage4()}

                            <div className="mt-6 flex justify-between items-center">
                                <button
                                    onClick={() => {
                                        setCurrentStage(Math.max(1, currentStage - 1));
                                        setError('');
                                    }}
                                    disabled={currentStage === 1 || loading}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    ← Previous
                                </button>
                                
                                <div className="text-sm text-gray-600">
                                    Stage {currentStage} of 4
                                </div>
                                
                                <button
                                    onClick={handleStageSubmit}
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    {loading
                                        ? 'Saving...'
                                        : currentStage === 4
                                        ? '✓ Submit Application'
                                        : 'Next →'}
                                </button>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default ApplicationForm;
